'use client'

import React, { useRef, useState } from 'react'
import { track } from '@/utils/analytics'

/**
 * Web3Forms delivers applications straight to email. No database.
 *
 * Why it submits from the browser rather than through an API route:
 * Web3Forms rejects server-side requests on the free plan — verified 2026-07-31,
 * the endpoint returns "This method is not allowed. Use our API in client side or
 * contact support with server IP address (Pro plan is required)", and it sits
 * behind Cloudflare bot protection. A proxy route is therefore not an option, so
 * validation and the honeypot run here.
 *
 * The access key is public by design (Web3Forms' own dashboard says so) and ends
 * up in the client bundle whichever way it is supplied, so the env var exists for
 * rotation rather than secrecy, with a fallback so the form works without any
 * Vercel configuration.
 */
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit'
const WEB3FORMS_ACCESS_KEY =
  process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY ?? 'ed0ef932-2fb4-4dfb-ad49-891434232b60'

const CATEGORIES = [
  { value: 'skincare', label: 'Skincare' },
  { value: 'haircare', label: 'Haircare' },
  { value: 'cosmetics', label: 'Colour cosmetics' },
  { value: 'oral-care', label: 'Oral care' },
  { value: 'food', label: 'Food' },
  { value: 'beverage', label: 'Beverage' },
  { value: 'household', label: 'Household' },
  { value: 'baby', label: 'Baby & child' },
  { value: 'supplements', label: 'Supplements' },
  { value: 'pet', label: 'Pet' },
  { value: 'other', label: 'Other' },
]

const REVENUE_BANDS = [
  'Pre-revenue',
  'Under $500k',
  '$500k – $2M',
  '$2M – $10M',
  '$10M – $50M',
  'Over $50M',
]

type Status = 'idle' | 'sending' | 'done' | 'error'

/**
 * Fallback address for when the browser cannot reach Web3Forms at all.
 *
 * Not hypothetical: Web3Forms sits behind Cloudflare bot protection, and an
 * end-to-end test of this form on 2026-07-31 saw a headless browser blocked
 * outright, with the failure surfacing as a CORS error because a challenge page
 * carries no Access-Control-Allow-Origin. Real users on VPNs, locked-down
 * corporate networks or privacy-hardened browsers can hit the same wall.
 *
 * Without a fallback their application is simply lost, and this is a B2B lead
 * worth hundreds of dollars — so a network failure hands them a prefilled mailto
 * rather than "please try again".
 */
const FALLBACK_EMAIL = 'team@microplasticsapp.com'

export default function CertificationForm() {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [mailtoFallback, setMailtoFallback] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>([])
  // One start event per session, on first interaction, so the funnel has a
  // denominator without firing on every keystroke.
  const startedRef = useRef(false)

  function noteStart() {
    if (startedRef.current) return
    startedRef.current = true
    track('certification_application_start', { cta_surface: 'certification_form' })
  }

  function toggleCategory(value: string) {
    noteStart()
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    )
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'sending') return

    const form = new FormData(e.currentTarget)
    const brand = String(form.get('brand_name') ?? '').trim()
    const contactName = String(form.get('contact_name') ?? '').trim()
    const contactEmail = String(form.get('contact_email') ?? '').trim()

    // Client-side validation is the only validation available here, since a
    // server proxy is not possible on the free plan.
    if (!brand || !contactName || !contactEmail.includes('@')) {
      setStatus('error')
      setError('Brand name, your name and a valid work email are required.')
      return
    }

    setStatus('sending')
    setError(null)

    const yesNo = (v: FormDataEntryValue | null) => (v === 'on' ? 'Yes' : 'No')

    // Field names become the labels in the delivered email, so they are written
    // for a human reading it rather than for a database column.
    const payload = new FormData()
    payload.append('access_key', WEB3FORMS_ACCESS_KEY)
    payload.append('subject', `Certification application: ${brand}`)
    payload.append('from_name', 'MicroPlastics certification form')
    payload.append('replyto', contactEmail)

    payload.append('Brand', brand)
    payload.append('Contact name', contactName)
    payload.append('Contact email', contactEmail)
    payload.append('Website', String(form.get('website') ?? '') || '—')
    payload.append('Country', String(form.get('country') ?? '') || '—')
    payload.append('Annual revenue band', String(form.get('annual_revenue_band') ?? '') || 'Not stated')
    payload.append('Products to assess', String(form.get('product_count') ?? '') || '—')
    // Send the display labels, not the internal slugs. The delivered email read
    // "skincare, haircare" because `categories` holds the slug values used by the
    // chip buttons; a human reading the application should see "Skincare,
    // Haircare".
    const categoryLabels = categories
      .map((value) => CATEGORIES.find((c) => c.value === value)?.label ?? value)
      .join(', ')
    payload.append('Product categories', categoryLabels || '—')
    payload.append('Declares no microplastic ingredients', yesNo(form.get('declares_ingredients')))
    payload.append('Declares low-plastic packaging', yesNo(form.get('declares_packaging')))
    payload.append('Ingredient list URL', String(form.get('ingredient_list_url') ?? '') || '—')
    payload.append('Notes', String(form.get('notes') ?? '') || '—')
    payload.append('Submitted from', typeof window !== 'undefined' ? window.location.href : 'unknown')

    // Web3Forms' native honeypot: it rejects the submission server-side if this
    // field arrives non-empty, which is stronger than checking it ourselves.
    payload.append('botcheck', String(form.get('botcheck') ?? ''))

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: payload,
        headers: { Accept: 'application/json' },
      })
      const json = (await res.json()) as { success?: boolean; message?: string }

      if (!res.ok || !json.success) {
        setStatus('error')
        setError(json.message ?? 'We could not send your application. Please try again shortly.')
        track('certification_application_error', {
          cta_surface: 'certification_form',
          error_status: res.status,
        })
        return
      }

      setStatus('done')
      track('certification_application_submit', {
        cta_surface: 'certification_form',
        category_count: categories.length,
        revenue_band: String(form.get('annual_revenue_band') ?? 'unspecified'),
      })
    } catch {
      setStatus('error')
      setError(
        'Your network or browser blocked the submission. Nothing was sent — use the email link below and your details are already filled in.'
      )
      // Rebuild the application as an email body so the work is not wasted.
      const lines: string[] = []
      payload.forEach((value, key) => {
        if (['access_key', 'botcheck', 'from_name', 'replyto', 'subject'].includes(key)) return
        lines.push(`${key}: ${String(value)}`)
      })
      setMailtoFallback(
        `mailto:${FALLBACK_EMAIL}` +
          `?subject=${encodeURIComponent(`Certification application: ${brand}`)}` +
          `&body=${encodeURIComponent(lines.join('\n'))}`
      )
      track('certification_application_error', {
        cta_surface: 'certification_form',
        error_status: 0,
      })
    }
  }

  if (status === 'done') {
    return (
      <div className="rounded-2xl border border-forest/30 bg-forest/[0.06] p-6 sm:p-8">
        <h3 className="font-display text-2xl font-bold text-ink">Application received</h3>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Thank you. We review applications in the order they arrive and will be
          in touch at the email you provided with the assessment scope and the
          fee tier for your revenue band.
        </p>
        <p className="mt-3 text-sm text-gray-600">
          Nothing is published, and no mark may be used, until an assessment is
          complete and approved.
        </p>
      </div>
    )
  }

  const inputClass =
    'w-full rounded-xl border border-ink/15 px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-forest/40 focus:border-forest'
  const labelClass = 'block text-sm font-semibold text-ink mb-1.5'

  return (
    <form onSubmit={handleSubmit} onFocus={noteStart} className="ph-no-capture space-y-6">
      {/* Web3Forms' own honeypot field name. Filled = rejected on their side. */}
      <div aria-hidden="true" className="hidden">
        <label htmlFor="botcheck">Do not fill this in</label>
        <input id="botcheck" name="botcheck" type="checkbox" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass} htmlFor="brand_name">Brand name *</label>
          <input id="brand_name" name="brand_name" required maxLength={200} className={inputClass} placeholder="Acme Skincare" />
        </div>
        <div>
          <label className={labelClass} htmlFor="website">Website</label>
          <input id="website" name="website" type="url" maxLength={500} className={inputClass} placeholder="https://" />
        </div>
        <div>
          <label className={labelClass} htmlFor="contact_name">Your name *</label>
          <input id="contact_name" name="contact_name" required maxLength={200} className={inputClass} />
        </div>
        <div>
          <label className={labelClass} htmlFor="contact_email">Work email *</label>
          <input id="contact_email" name="contact_email" type="email" required maxLength={320} className={inputClass} />
        </div>
        <div>
          <label className={labelClass} htmlFor="country">Country</label>
          <input id="country" name="country" maxLength={100} className={inputClass} />
        </div>
        <div>
          <label className={labelClass} htmlFor="annual_revenue_band">
            Annual revenue band
          </label>
          <select id="annual_revenue_band" name="annual_revenue_band" className={inputClass} defaultValue="">
            <option value="">Prefer not to say</option>
            {REVENUE_BANDS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-gray-500">
            Fees are on a sliding scale by revenue, so small brands are not
            priced out. This only sets your tier.
          </p>
        </div>
        <div>
          <label className={labelClass} htmlFor="product_count">Products to assess</label>
          <input id="product_count" name="product_count" type="number" min={1} max={100000} className={inputClass} placeholder="12" />
        </div>
        <div>
          <label className={labelClass} htmlFor="ingredient_list_url">
            Link to ingredient lists
          </label>
          <input id="ingredient_list_url" name="ingredient_list_url" type="url" maxLength={500} className={inputClass} placeholder="https://" />
        </div>
      </div>

      <fieldset>
        <legend className={labelClass}>Product categories</legend>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const on = categories.includes(c.value)
            return (
              <button
                type="button"
                key={c.value}
                onClick={() => toggleCategory(c.value)}
                aria-pressed={on}
                className={`text-sm px-3.5 py-2 rounded-full border transition-colors ${
                  on
                    ? 'bg-forest text-white border-forest'
                    : 'bg-white text-gray-700 border-ink/15 hover:border-forest/40'
                }`}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-2xl border border-ink/10 p-5">
        <legend className="px-2 text-sm font-semibold text-ink">What are you claiming?</legend>
        <label className="flex items-start gap-3 text-sm text-gray-700">
          <input type="checkbox" name="declares_ingredients" className="mt-1 h-4 w-4 accent-[#1E6B47]" />
          <span>
            <strong>No microplastic ingredients.</strong> None of the products in
            scope contain solid synthetic polymer particles or plastic
            film-formers.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-gray-700">
          <input type="checkbox" name="declares_packaging" className="mt-1 h-4 w-4 accent-[#1E6B47]" />
          <span>
            <strong>Low-plastic packaging.</strong> Products in scope ship in
            glass, metal, or another format that does not put a polymer wall
            against the contents.
          </span>
        </label>
        <p className="text-xs text-gray-500">
          These are your declarations, not our findings. Everything is verified
          against the published criteria before any mark is granted, and a brand
          can be certified on one axis without the other.
        </p>
      </fieldset>

      <div>
        <label className={labelClass} htmlFor="notes">Anything else</label>
        <textarea id="notes" name="notes" rows={4} maxLength={4000} className={inputClass} placeholder="Reformulation timelines, packaging changes in progress, questions about scope." />
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
          {mailtoFallback && (
            <a
              href={mailtoFallback}
              className="mt-2 inline-flex min-h-[44px] items-center font-semibold text-red-800 underline"
            >
              Send it by email instead
            </a>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="inline-flex items-center justify-center bg-ink text-cream px-7 py-3.5 rounded-control font-semibold hover:bg-ink/90 disabled:opacity-60 disabled:cursor-not-allowed transition-[color,background-color,border-color,text-decoration-color] min-h-[48px] pressable"
      >
        {status === 'sending' ? 'Submitting…' : 'Submit application'}
      </button>

      <p className="text-xs text-gray-500 leading-relaxed">
        We use your details to assess the application and contact you about it.
        Applications are not published. See our{' '}
        <a href="/privacy" className="underline">privacy policy</a>.
      </p>
    </form>
  )
}
